import "./login.scss";
import { toast } from "react-toastify";
import loginlogo from "../../assets/images/login-logo.svg";
import awsLogo from "../../assets/images/Powered-by-AWS.png";
import loginimage from "../../assets/images/login-image.jpg";
import Form from "react-bootstrap/Form";
import { Button, Col, Container, Row } from "react-bootstrap";
import { useCallback, useEffect, useState } from "react";
import * as Yup from "yup";
import { useFormik, FormikHelpers } from "formik";
import type { Credentials } from "../../redux/auth/authApiSlice";
import useAuth from "../../Hooks/useAuth";
import { Link, useNavigate } from "react-router-dom";
import useAdmin from "../../Hooks/useAdmin";
import {
  setCredentials,
  setMenuItems,
  setPermissions,
} from "../../redux/auth/authSlice";
import { useAppDispatch } from "../../redux/hooks";
import useRolesAndPermissions from "../../Hooks/useRolesAndPermissions";
import useCustomFields from "../../Hooks/useCustomFields";
import { setCustomFieldsMetadata } from "../../redux/customFields/customFieldsSlice";
import CustomModal from "../../components/customModal/CustomModal";
import { handleNumberInput } from "../../utils/commonUtils";

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const initialIsRemember = localStorage.getItem("rememberMe") === "true";
  const [isLoginError, setIsLoginError] = useState(false);
  const [loginError, setLoginError] = useState(null);
  const [apiVesrion, setApiVesrion] = useState<null | string>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [isRememberMe, setIsRememberMe] = useState(initialIsRemember);
  const [showApiVersionModal, setShowApiVersionModal] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpValue, setOtpValue] = useState("");
  const [otpError, setOtpError] = useState<null | string>(null);
  const [temporaryLoginResult, setTemporaryLoginResult] = useState<any>(null);
  const { getPermissionsAPI } = useAdmin();
  const { getCustomFieldsMetadataAPI } = useCustomFields();
  const { getRolesPermissionAPI } = useRolesAndPermissions();
  const {
    loginAction,
    loginLoading,
    getAPIVersionAPI,
    verifyOTPApi,
    verifyOTPLoading,
  } = useAuth();

  const { handleChange, handleBlur, values, handleSubmit, touched, errors, setFieldValue } =
    useFormik({
      initialValues: {
        username: isRememberMe ? localStorage.getItem("username") || "" : "",
        password: "",
        clientid: isRememberMe
          ? (localStorage.getItem("profileClientId") || "").toUpperCase()
          : "",
        ipAddress: "",
      },
      validationSchema: Yup.object({
        username: Yup.string().required("Email / Username is Required"),
        password: Yup.string().required("Password is Required"),
        clientid: Yup.string().required("Client ID is Required"),
      }),
      onSubmit: async (
        values: Credentials,
        { setSubmitting }: FormikHelpers<Credentials>
      ) => {
        try {
          setClientId();

          for (let attempt = 1; attempt <= 3; attempt++) {
            try {
              const res = await loginAction(values);
              const { result, message } = res;

              if (result?.otpRequired) {
                setTemporaryLoginResult(result);
                setShowOtpModal(true);
                setOtpValue("");
                setOtpError(null);
                return;
              }
              dispatch(setCredentials(result));
              await handleLoginSuccess(result, message);
              return;
            } catch (err: any) {
              const isTenantError = err?.message?.toLowerCase() === "tenantinfo is not valid";
              const isLastAttempt = attempt === 3;

              if (!isTenantError || isLastAttempt) {
                throw err;
              }
            }
          }
        } catch (err: any) {
          setIsLoginError(true);
          setLoginError(err.message);
        } finally {
          setSubmitting(false);
        }
      },
    });

  const handleOtpSubmit = async () => {
    if (!otpValue.trim()) {
      setOtpError("OTP is required");
      return;
    }

    try {
      setOtpError(null);
      const otpVerificationRes = await verifyOTPApi({
        username: temporaryLoginResult.username,
        otp: otpValue,
        ipAddress: values.ipAddress,
        tenantId: values.clientid,
      });

      if (otpVerificationRes?.success) {
        dispatch(setCredentials(otpVerificationRes?.result));
        await handleLoginSuccess(
          otpVerificationRes?.result,
          "OTP Verified. Login Successful."
        );
        setShowOtpModal(false);
        setOtpValue("");
        setTemporaryLoginResult(null);
      } else {
        setOtpError(
          otpVerificationRes?.message ||
          "OTP verification failed. Please try again."
        );
      }
    } catch (err: any) {
      setOtpError(err.message || "OTP verification failed. Please try again.");
    }
  };

  const handleCloseOtpModal = () => {
    setShowOtpModal(false);
    setOtpValue("");
    setOtpError(null);
  };

  const getMenuItems = (id: string) => {
    return getPermissionsAPI(id).then((res: any) => {
      dispatch(setMenuItems(res));
      return res;
    });
  };

  const getPermissions = (id: string) => {
    return getRolesPermissionAPI({ roleId: id }).then((data: any) => {
      const groupedData = data
        .map((item: { permission: any }) => item.permission)
        .filter(
          (x: { isMenu: boolean; isActive: boolean }) =>
            x.isMenu === false && x.isActive === true
        );
      dispatch(setPermissions(groupedData));
      return groupedData;
    });
  };

  const getCustomFieldsMeta = () => {
    return getCustomFieldsMetadataAPI({}).then((resCustomMetadata) => {
      dispatch(setCustomFieldsMetadata(resCustomMetadata));
      return resCustomMetadata;
    });
  };

  const handleLoginSuccess = async (result: any, message?: string) => {
    try {
      const token = result?.jwtToken || localStorage.getItem("jwtToken") || "";

      localStorage.setItem("profilePicture", result?.profilePicture);
      localStorage.setItem("profileClientId", values.clientid);
      setIsLoginError(false);
      setLoginError(null);

      if (token) {
        await Promise.all([
          getMenuItems(result.role.id),
          getPermissions(result.role.id),
          getCustomFieldsMeta(),
        ]);

        navigate("/");
        toast.success(message || "Login Successful.");
      }
    } catch (err: any) {
      throw err;
    }
  };

  const setClientId = () => {
    if (isRememberMe) {
      localStorage.setItem("profileClientId", values.clientid);
      localStorage.setItem("username", values.username);
      localStorage.setItem("rememberMe", "true");
    } else {
      localStorage.removeItem("profileClientId");
      localStorage.removeItem("username");
      localStorage.setItem("rememberMe", "false");
    }
  };

  const convertToUpperCase = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.currentTarget;
    const cursorPosition = input.selectionStart || 0;
    const upperCaseValue = input.value.toUpperCase();

    setFieldValue("clientid", upperCaseValue);

    requestAnimationFrame(() => {
      try {
        input.setSelectionRange(cursorPosition, cursorPosition);
      } catch (err) {
        console.error("Error setting cursor position:", err);
      }
    });
  };

  const fetchIP = useCallback(async () => {
    const res = await fetch("https://api.ipify.org?format=json");
    const data = await res.json();
    setFieldValue("ipAddress", data.ip);
  }, [setFieldValue]);

  const fetchAPIVersion = useCallback(async () => {
    const res = await getAPIVersionAPI(values);
    setApiVesrion(res?.result || null);
  }, [getAPIVersionAPI]);

  useEffect(() => {
    setIsRememberMe(localStorage.getItem("rememberMe") === "true");
    fetchIP();
    fetchAPIVersion();
  }, []);

  const handleToggleApiVersionModal = () => {
    setShowApiVersionModal((prev) => !prev);
  };

  return (
    <div className="login-wrapper">
      <Container fluid>
        <Row style={{ minHeight: "100vh" }}>
          <Col xs={12} md={6}>
            <div className="login-form-wrapper">
              <figure>
                <img src={loginlogo} alt="logo" />
              </figure>
              <div className="login-title-wrapper">
                <h4>Sign in</h4>
                <p>Welcome back! Please sign in to continue.</p>
              </div>
              <Form noValidate onSubmit={handleSubmit}>
                <Form.Group className="mb-4">
                  <Form.Label>Client ID</Form.Label>
                  <Form.Control
                    id="clientid"
                    name="clientid"
                    type="text"
                    placeholder="Enter Client ID"
                    onChange={(e) => {
                      convertToUpperCase(e as any);
                      setLoginError(null);
                    }}
                    value={values.clientid}
                    isInvalid={touched.clientid && !!errors.clientid}
                    onBlur={(e) => {
                      handleBlur(e);
                      setFieldValue("clientid", e.target.value.trim());
                    }}
                  />

                  {touched.clientid && errors.clientid ? (
                    <Form.Control.Feedback type="invalid">
                      {errors.clientid}
                    </Form.Control.Feedback>
                  ) : null}
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label>Email / Username</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter Email / Username"
                    required
                    id="username"
                    name="username"
                    onChange={(e) => {
                      handleChange(e);
                      setLoginError(null);
                    }}
                    value={values.username}
                    isInvalid={touched.username && !!errors.username}
                    onBlur={(e) => {
                      handleBlur(e);
                      setFieldValue("username", e.target.value.trim());
                    }}
                  />
                  {touched.username && errors.username ? (
                    <Form.Control.Feedback type="invalid">
                      {errors.username}
                    </Form.Control.Feedback>
                  ) : null}
                </Form.Group>

                <Form.Group className="mb-4 password-field-box">
                  <Form.Label>Password</Form.Label>
                  <Form.Control
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter Password"
                    onChange={(e) => {
                      handleChange(e);
                      setLoginError(null);
                    }}
                    value={values.password}
                    isInvalid={touched.password && !!errors.password}
                  />
                  {!errors.password ? (
                    showPassword ? (
                      <i
                        className="bi bi-eye-fill"
                        onClick={() => setShowPassword(!showPassword)}
                      />
                    ) : (
                      <i
                        className="bi bi-eye-slash-fill"
                        onClick={() => setShowPassword(!showPassword)}
                      />
                    )
                  ) : null}

                  {touched.password && errors.password ? (
                    <Form.Control.Feedback type="invalid">
                      {errors.password}
                    </Form.Control.Feedback>
                  ) : null}
                  {isLoginError ? (
                    <span className="login-error"> {loginError}</span>
                  ) : null}
                </Form.Group>

                <Form.Group
                  className="mb-4 forgot-pass-link-box"
                  style={{ textAlign: "right" }}
                >
                  <span className="remember-me-check">
                    <Form.Check
                      inline
                      type="checkbox"
                      id="remember-me"
                      checked={isRememberMe}
                      onChange={() => setIsRememberMe(!isRememberMe)}
                    />
                    Remember Me
                  </span>
                  <Button variant="link" href="/forgot-password">
                    Forgot Password?
                  </Button>
                </Form.Group>
                <Form.Group className="d-grid">
                  <Button
                    disabled={loginLoading}
                    type="submit"
                    variant="primary"
                  >
                    Sign In
                  </Button>
                </Form.Group>
              </Form>
              <div className="login-bottom-wrapper">
                <img src={awsLogo} alt="aws" />
                <span className="copy-text">
                  Copyright © {new Date().getFullYear()} TriNet Medical, LLC. All
                  rights reserved.
                </span>
                <span onDoubleClick={handleToggleApiVersionModal}>
                  Version: 2026.03.24
                </span>
              </div>
              {showApiVersionModal && (
                <CustomModal
                  id="api-version-modal"
                  title="API Version"
                  content={
                    <p style={{ marginBottom: 0 }}>
                      {apiVesrion || (
                        <span style={{ color: "red" }}>
                          API version not found.
                        </span>
                      )}
                    </p>
                  }
                  onClose={handleToggleApiVersionModal}
                />
              )}
              {showOtpModal && (
                <CustomModal
                  id="otp-verification-modal"
                  title="Two-Factor Authentication"
                  content={
                    <div>
                      <p style={{ marginBottom: "1rem" }}>
                        Please enter the OTP sent to your registered email or
                        phone.
                      </p>
                      <Form.Group className="mb-3">
                        <Form.Label>Enter OTP</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Enter OTP"
                          value={otpValue}
                          onChange={(e) => {
                            setOtpValue(e.target.value);
                            setOtpError(null);
                          }}
                          onInput={handleNumberInput}
                          isInvalid={!!otpError}
                        />
                        {otpError && (
                          <Form.Control.Feedback type="invalid">
                            {otpError}
                          </Form.Control.Feedback>
                        )}
                      </Form.Group>
                      <div style={{ display: "flex", gap: "0.5rem" }}>
                        <Button
                          variant="primary"
                          onClick={handleOtpSubmit}
                          className="flex-grow-1"
                        >
                          Verify OTP
                        </Button>
                        <Button
                          variant="secondary"
                          onClick={handleCloseOtpModal}
                          className="flex-grow-1"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  }
                  onClose={handleCloseOtpModal}
                />
              )}
            </div>
          </Col>
          <Col
            style={{
              backgroundImage: `url(${loginimage})`,
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center center",
              backgroundSize: "cover",
            }}
            xs={12}
            md={6}
          >
            <div className="login-image-wrapper">
              <h4>Company</h4>
              <h1>Overview</h1>
              <p>
                TriNet Medical provides Healthcare IT solutions with
                quantifiable results in the areas of Data Management services,
                Technology Services and Barcode Integration with EMR.
              </p>
              <p>
                We deliver highly valuable and innovative solutions that bridge
                our client's execution gaps through a clear understanding of
                their business needs and a culture of shared commitments.
              </p>
              <div className="url-link-box">
                <Link
                  className="text-primary-700"
                  to="https://www.trinetmedical.com/"
                  target="_blank"
                >
                  www.trinetmedical.com
                </Link>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Login;
